// Angular
import { Injectable, Inject } from '@angular/core';

// NGRX && RXJS
import { map, take } from 'rxjs/operators';
import { Store, select } from '@ngrx/store';
import { Observable, of, forkJoin } from 'rxjs';

// OLCI
import * as _ from 'lodash';
import * as utils from '../../../util';
import * as fromRoot from '../../../reducers';
import { GuestService } from './guest.service';
import { GatingServices } from './gating.services';
import { GrantorInfo, Guest } from '../../../models';
import { CONSTANTS } from '../../../constants/constants';
import { FeatureFlagsService } from './feature-flags.service';
import { MicroServicesService } from './micro-services.service';
import { GuestReservationId } from '../../../models/arrivalAppointment';
import { IDeleteAuthorizedConsentsResponse } from '../../../models/delete-authorized-consents.payload';
import { ITravelQuestionnairePayload, IBookingGuestTypes, ISaveTravelQuestionnairePayload, ITypes } from '../../../models/travel-questionnaire.payload';
import { DeleteBiometricsAuthorizationsSuccessAction, SaveAuthorizationConsentsSuccessAction, CreateUpdateTravelQuestionnaireActionSuccess } from '../../../actions';
import { IAuthorizedConsentsGrantee, IAuthorizedConsentsParams, IAuthorizedConsentsPayload, IAuthorizedConsentsSaveGrantee, IAuthorizedConsentsSavePayload, IContent, IContractDocuments } from '../../../models/authorized-consents.payload';

@Injectable()
export class ContractsService {
	grantor: any = null;

	constructor(
		private readonly store: Store<fromRoot.AppState>,
		private guestsService: GuestService,
		private gatingService: GatingServices,
		private microServices: MicroServicesService,
		private readonly featureFlagService: FeatureFlagsService,
		@Inject(CONSTANTS) private constants: any
	) { }

	get contractsList(): string[] {
		return [
			'RESERVATION CONTRACT', 'BIOMETRIC CONSENT', 'FACIAL BIO CONSENT', 'PHOTO BIO CONSENT GRANTEE', 'PHOTO BIO CONSENT GRANTOR', 'FACIAL BIO CONSENT GRANTEE', 'FACIAL BIO CONSENT GRANTOR', 'HQ1', 'HQ2', 'HEALTH RISK ACKNOWLEDGEMENT'
		]
	}

	grantorInformation(fakeGrantor?: Guest): GrantorInfo {
		const legalGrantor: Guest = fakeGrantor || this.getLegalGrantor();
		const vdsInfo = this.gatingService.getLoggedInGuestInfo();
		return {
			bookingId: legalGrantor.bookingId,
			firstName: legalGrantor.firstName,
			lastName: legalGrantor.lastName,
			guestId: legalGrantor.guestId,
			country: legalGrantor.countryCode as string,
			vdsId: vdsInfo.vdsId
		} as GrantorInfo;
	}

	isCruiseContractGrantor(guestId: number): boolean {
		return this.isGrantorOf(guestId, this.constants.CONSENTS_DOCUMENTS_TYPE.CTC);
	}

	isBiometricsGrantor(guestId: number): boolean {
		return this.isGrantorOf(guestId, this.constants.CONSENTS_DOCUMENTS_TYPE.PHOTO_GRANTOR);
	}

	isAdcFacialGrantor(guestId: number): boolean {
		return this.isGrantorOf(guestId, this.constants.CONSENTS_DOCUMENTS_TYPE.FACIAL_GRANTOR);
	}

	isAdcFacialGrantee(guestId: number): boolean {
		return this.isGranteeOf(guestId, this.constants.CONSENTS_DOCUMENTS_TYPE.FACIAL_GRANTEE);
	}

	isBiometricsGrantee(guestId: number): boolean {
		return this.isGranteeOf(guestId, this.constants.CONSENTS_DOCUMENTS_TYPE.PHOTO_GRANTEE);
	}

	isGranteeOf(guestId: number, consentType: string): boolean {
		let result = false;
		this.store
			.pipe(
				select(fromRoot.getGrantees),
				take(1)
			)
			.subscribe(grantees => {
				if (grantees?.length) {
					const guest = grantees.find((g: any) => g.guestReservationId.guestId === guestId);
					if (guest) {
						if (guest.signedDocuments && guest.signedDocuments.length) {
							result = !!guest.signedDocuments.find((sd: any) => sd.type === utils.removeUnderscore(consentType))
						}
					}
				}
			});
		return result;
	}

	isGrantorOf(guestId: number, consentType: string): boolean {
        let result = false;
        this.store
           .pipe(
               select(fromRoot.getGrantees),
               take(1)
           )
           .subscribe(grantees => {
               if (grantees && grantees.length > 0) {
                   const guest = grantees.find((g: any) => g.grantor && g.grantor.guestReservationId.guestId === guestId);
                   if (guest && guest.signedDocuments && guest.signedDocuments.length) {
                       result = !!guest.signedDocuments.find((sd: any) => sd.type === utils.removeUnderscore(consentType));
                    }
                }
             });
        return result;
    } 

	getLegalGrantor(): Guest {
		const paxId = this.guestsService.loggedInGuestPaxId;
		const guest = paxId ? this.guestsService.getGuest(paxId) : this.guestsService.getFirstAdultInTheReservation();
		if (!guest) {
			throw new Error('No legal grantor could be determined.');
		}
		return guest;
	}

	findConsentInfoForGuest(consents: any, guestId: any) {
		const consentInfoForGuest = consents.find((g: any) => g.granteeInfo.guestId === guestId);
		if (consentInfoForGuest.grantorInfo !== null) {
			this.grantor = consentInfoForGuest.grantorInfo;
		}
		return this.grantor;
	}

	findOldBiometricsBySelectedGuest(guestId: any): IContractDocuments[] {
		let consentListToDelete: IContractDocuments[] = [];
		this.store
			.pipe(
				select(fromRoot.getGrantees),
				take(1)
			)
			.subscribe(grantees => {
				if (grantees?.length) {
					const guest = grantees.find((g: any) => g.guestReservationId.guestId === guestId);
					if (guest) {
						consentListToDelete = guest.signedDocuments ? guest.signedDocuments.filter((sd: any) => sd.type === (utils.removeUnderscore(this.constants.CONSENTS_DOCUMENTS_TYPE.BIOMETRIC) || utils.removeUnderscore(this.constants.CONSENTS_DOCUMENTS_TYPE.FACIAL))) : [];
					}
				}
			});
		return consentListToDelete;
  }

    geOldtLoadCtcPayload(): any {
		let payload = null;
		this.store
			.pipe(
				select(fromRoot.getEntitySGuests),
				take(1)
			)
			.subscribe(res => {
				if (res && res.length > 0) {
					let guest = res.find((g: any) => g.bookingOfficeCode === 'MIA');
					if (!guest) {
						guest = res[0];
					}
					payload = {
						bookingId: guest.bookingId,
						guestId: guest.guestId
					};
				}
			});
		return payload;
	}

	getLoadCtcPayload(types: string[]): IAuthorizedConsentsPayload {
		const payload: IAuthorizedConsentsPayload = { authorizedConsents: [] };
		this.store
			.pipe(
				select(fromRoot.getEntitySGuests),
				take(1)
			)
			.subscribe(guests => {
				if (guests && guests.length > 0) {
					guests.forEach((g: any) => {
						const bookingGuestTypes: IBookingGuestTypes = { bookingId: g.bookingId, guestId: g.guestId, types: types };
						payload.authorizedConsents.push(bookingGuestTypes);
					})
				}
			});
		return payload;
	}

	invalidateBiometrics(guestId: number): Observable<boolean> {
		const contractTypes: string[] = [
			this.constants.CONSENTS_DOCUMENTS_TYPE.PHOTO_GRANTEE,
			this.constants.CONSENTS_DOCUMENTS_TYPE.FACIAL_GRANTEE,
			this.constants.CONSENTS_DOCUMENTS_TYPE.PHOTO_GRANTOR,
			this.constants.CONSENTS_DOCUMENTS_TYPE.FACIAL_GRANTOR
		];
		return this.invalidateConsents(guestId, contractTypes);
	}

	invalidateCruiseContract(guestId: number) {
		const contractTypes: string[] = [this.constants.CONSENTS_DOCUMENTS_TYPE.CTC];
		const payload = this.invalidateConsentPayload(guestId, contractTypes);
		this.store.dispatch(new DeleteBiometricsAuthorizationsSuccessAction(payload));
	}

	get areAuthorizationsInTheStore(): boolean {
		let result = false;
		this.store
			.pipe(
				select(fromRoot.getGrantees),
				take(1)
			)
			.subscribe(consents => {
				result = consents.length > 0;
			});

		return result;
	}

	getGrantor(granteeId: number): number[] {
		const result: any = [];

		this.store
			.pipe(
				select(fromRoot.getGrantees),
				take(1)
			)
			.subscribe(grantees => {
				const guest = grantees?.find((g: any) => g.guestReservationId.guestId === granteeId);
				if (guest && guest.grantor) {
					result.push(guest.grantor.guestReservationId.guestId);
				}
			});

		return result;
	}

	saveConsents(
		guestsId: number[],
		contractTypes: string[],
		contractTypesToDelete: string[],
		fakeGrantor?: Guest
	): Observable<boolean[]> {
		const guestId = guestsId[0];
		return forkJoin([this.addConsents({guestsId, contractTypes, fakeGrantor} as IAuthorizedConsentsParams), this.deleteConsents(guestId, contractTypesToDelete)]);
	}

	addConsents({guestsId, contractTypes, country, fakeGrantor}: IAuthorizedConsentsParams): Observable<boolean> {
		if (!contractTypes.length) {
			return of(true);
		}
		const contractsStore: any = [];
		contractTypes.forEach(c => {
			contractsStore.push(utils.removeUnderscore(c));
		})
		const payload = this.createConsentPayload(guestsId, contractsStore, country as string, fakeGrantor);
		if (!payload.grantees.length) {
			return of(true);
		}
		return this.microServices.createUpdateAuthorizedConsents(payload).pipe(map(r => {
			if (r.status === this.constants.HTTP_STATUS_CODE.SUCCESS) {
				const response = Object.assign({}, r, {
					payload: Object.assign({}, r.payload, {
						contractTypes: contractsStore
					})
				})
				this.store.dispatch(new SaveAuthorizationConsentsSuccessAction(response));
				return true;
			}
			return false;
		}))
	}

	deleteConsents(guestId: number, contractTypes: string[]): Observable<boolean> {
		const contractsToDelete = this.contractTypesToDelete(guestId, contractTypes);
		if (!contractsToDelete.length) {
			return of(true);
		}
		const payload = this.deleteConsentPayload(guestId, contractsToDelete);
		return this.microServices.deleteAuthorizedConsents(payload).pipe(map(r => this.dispatchDeleteBiometricsAuthorizations(r, payload)));
	}

	invalidateConsents(guestId: number, contractTypes: string[]): Observable<boolean> {
		const contractsToDelete = this.contractTypesToDelete(guestId, contractTypes);
		if (!contractsToDelete.length) {
			return of(true);
		}
		const payload = this.invalidateConsentPayload(guestId, contractsToDelete);
		return this.microServices.deleteAuthorizedConsents(payload).pipe(map(r => this.dispatchDeleteBiometricsAuthorizations(r, payload)));
	}

  private dispatchDeleteBiometricsAuthorizations(r: IDeleteAuthorizedConsentsResponse, payload:  {authorizedConsents: any[]}) {
    if (r.status === this.constants.HTTP_STATUS_CODE.SUCCESS) {
      this.store.dispatch(new DeleteBiometricsAuthorizationsSuccessAction(payload));
      return true;
    }
    return false;
  }

	isGuestFromInternationalBookingOffice(guestId: number): boolean {
		const guestInfo = this.guestsService.getGuest(guestId);

		return guestInfo.bookingOfficeCode !== this.constants.BOOKING_OFFICE.MIA;
	}

	granteesArray(guestsId: number[], contractTypes: string[], country: string): IAuthorizedConsentsSaveGrantee[] {
		let granteesStore: Partial<IAuthorizedConsentsGrantee[]> = [];
		const result: IAuthorizedConsentsSaveGrantee[] = [];
		if (this.validateContractTypesAndGuestsExists(guestsId, contractTypes)) {
			return result;
		}

		this.store
			.pipe(
				select(fromRoot.getGrantees),
				take(1)
			)
			.subscribe(g => {
				granteesStore = g;
			});

		guestsId.forEach(guestId => {
			const guestStoreInfo = granteesStore.find(g => g?.guestReservationId.guestId === guestId);
			let documents: IContractDocuments[] = [];
			if (guestStoreInfo) {
				const guestReservationId: GuestReservationId = {
					bookingId: guestStoreInfo.guestReservationId.bookingId,
					guestId: guestStoreInfo.guestReservationId.guestId,
					country
				}
        const isInternational = this.isGuestFromInternationalBookingOffice(guestId);
        documents = this.contractTypesLoopProcess(
          documents,
          contractTypes,
          guestStoreInfo,
          isInternational
        );

				if (documents.length) {
					result.push({ guestReservationId: guestReservationId, documents: documents });
				}
			}
		});

		return result;
	}

  private validateContractTypesAndGuestsExists(
    guestsId: number[], contractTypes: string[],
  ) {
    return !contractTypes.length || !guestsId.length;
  }

  private contractTypesLoopProcess(
    documents: IContractDocuments[],
    contractTypes: string[],
    guestStoreInfo: IAuthorizedConsentsGrantee,
    isInternational: boolean
  ) {
    contractTypes.forEach(contract => {
      if (this.getUnsignedDocumentsValidate(guestStoreInfo)) {
        if (this.getInternationalAndReservationContractValidate(isInternational, contract)) {
          return;
        }

        const document: IContractDocuments | any = guestStoreInfo.unsignedDocuments.find(g => g.type === contract);
        if (document) {
          const cleanDocument: IContractDocuments = {
            type: document.type,
            contents: document.contents.map((content: any) => {
              return {
                id: content.id,
                languageCode: content.languageCode,
                sequence: content.sequence,
                version: content.version
              }
            })
          }
          documents.push(cleanDocument);
        }
      }
    });
    return documents;
  }

  private getUnsignedDocumentsValidate(guestStoreInfo: IAuthorizedConsentsGrantee) {
    return guestStoreInfo.unsignedDocuments && guestStoreInfo.unsignedDocuments.length
  }

  getInternationalAndReservationContractValidate(
    isInternational: boolean,
    contract: string
  ) {
    const isCtcFromEdocsEnabled = this.featureFlagService.snapshot['olci-ctc-source'];

    return isInternational && contract === utils.removeUnderscore(this.constants.CONSENTS_DOCUMENTS_TYPE.CTC) && !isCtcFromEdocsEnabled;
  }

	createConsentPayload(guestsId: number[], contractTypes: string[], country: string, fakeGrantor?: Guest): IAuthorizedConsentsSavePayload | any {
		const grantor: GrantorInfo = this.grantorInformation(fakeGrantor);
		const grantees: IAuthorizedConsentsSaveGrantee[] = this.granteesArray(guestsId, contractTypes, country);
		if (!grantor) {
			return;
		}

		return {
			ipAddress: this.constants.IP_ADDRESS,
			grantor: {
				guestAccountId: grantor.vdsId,
				guestReservationId: {
					bookingId: grantor.bookingId,
					guestId: grantor.guestId
				}
			},
			grantees: grantees
		} as IAuthorizedConsentsSavePayload;
  }

  hasSignedConsent(signedDocuments: IContractDocuments[], consentType: string): boolean {
    return signedDocuments.some((document: IContractDocuments) =>  document.type === consentType)
  }

	deleteConsentPayload(guestId: number, contractTypes: string[]) {
		const consentListToDelete: any[] = [];
		let grantees: any[] = [];

		this.store
			.pipe(
				select(fromRoot.getGrantees),
				take(1)
			)
			.subscribe(contracts => {
				grantees = contracts.filter((grantee: IAuthorizedConsentsGrantee) => !!grantee.signedDocuments && grantee.guestReservationId.guestId === guestId);
			});
		contractTypes.forEach(consentType => {
      const consentTypeFormatted = utils.removeUnderscore(consentType);
			const granteeWithConsents: IAuthorizedConsentsGrantee = grantees.find((grantee: IAuthorizedConsentsGrantee) => this.hasSignedConsent(grantee.signedDocuments, consentTypeFormatted));
			if (granteeWithConsents) {
				consentListToDelete.push({
					bookingId: granteeWithConsents.guestReservationId.bookingId,
					guestId: guestId,
					type: consentTypeFormatted
				});
			}
		});
		return { authorizedConsents: consentListToDelete };
	}

	invalidateConsentPayload(guestId: number, contractTypes: string[]) {
		const consentListToDelete: any[] = [];
		let grantees: IAuthorizedConsentsGrantee[] = [];

		this.store
			.pipe(
				select(fromRoot.getGrantees),
				take(1)
			)
      .subscribe(contracts => {
				grantees = contracts.filter((grantee: IAuthorizedConsentsGrantee) => !!grantee.signedDocuments);
			});
		contractTypes.forEach(consentType => {
      const consentTypeFormatted = utils.removeUnderscore(consentType);
      const granteeWithConsents =
        grantees.find((grantee: IAuthorizedConsentsGrantee) => grantee.guestReservationId.guestId === guestId &&
          this.hasSignedConsent(grantee.signedDocuments, consentTypeFormatted));

			if (granteeWithConsents) {
				if (this.isGrantorOf(guestId, consentType)) {
					const relatedGrantees = grantees.filter(
            (grantee: IAuthorizedConsentsGrantee) => grantee.grantor && grantee.grantor.guestReservationId.guestId === guestId &&
              this.hasSignedConsent(grantee.signedDocuments, consentTypeFormatted));
					relatedGrantees.forEach((grantee: IAuthorizedConsentsGrantee) => {
						consentListToDelete.push({
							bookingId: grantee.guestReservationId.bookingId,
							guestId: grantee.guestReservationId.guestId,
							type: consentTypeFormatted
						});
					});
				} else {
					consentListToDelete.push({
						bookingId: granteeWithConsents.guestReservationId.bookingId,
						guestId: guestId,
						type: consentTypeFormatted
					});
				}
			}
		});
		return { authorizedConsents: consentListToDelete };
	}

	contractTypesToSave(guestId: number, contractTypes: string[]): string[] {
		const contractTypesToSave: any = [];
		contractTypes.forEach(consentType => {
			if (!this.isGranteeOf(guestId, consentType)) {
				return contractTypesToSave.push(consentType);
			}
		});
		return contractTypesToSave;
	}

	contractTypesToDelete(guestId: number, contractTypes: string[]): string[] {
		const contractTypesToDelete: any = [];
		contractTypes.forEach(consentType => {
			if (this.isGranteeOf(guestId, consentType)) {
				return contractTypesToDelete.push(consentType);
			}
		});
		return contractTypesToDelete;
	}

	//Travel questionnaire
	get areTravelQuestionnairesInTheStore(): boolean {
		const selectedGuestsForCheckin = this.guestsService.selectedForCheckin;
		let guestsWithoutQuestionnaire = 0;
		let result = false;
		this.store
			.pipe(
				select(fromRoot.getTravelQuestionnaire),
				take(1)
			)
			.subscribe(questionnaires => {
				if (!questionnaires || !questionnaires.length) {
					result = false;
				}

				selectedGuestsForCheckin.forEach((guest: any) => {
					const exist = questionnaires.find((q: any) => q.guestId === guest.guestId);
					if (!exist) {
						guestsWithoutQuestionnaire++;
					}
				});
				result = guestsWithoutQuestionnaire === 0;
			});

		return result;
	}

	get displayTravelQuestionnaireSection(): boolean {
		let result = true;
		this.store.pipe(
			select(fromRoot.getTravelQuestionnaire),
			take(1))
      .subscribe(questionnaire => {
        if (questionnaire) {
					result = !!questionnaire.length;
				}
				else {
					result = false;
				}
			});
		return result;
	}

	getTravelQuestionnairePayload(): ITravelQuestionnairePayload | any {
		const selectedGuestsForCheckin = this.guestsService.selectedForCheckin;
		const types = [this.constants.CONSENTS_DOCUMENTS_TYPE.HQ1];
		if (!selectedGuestsForCheckin.length) {
			return null;
		}
		const bookingIdGuestId: GuestReservationId[] = this.guestsService.getAllGuestsBookingCombination(selectedGuestsForCheckin);
		const bookingIdGuestIdTypes: IBookingGuestTypes[] = bookingIdGuestId.map(o => {
			return { guestId: o.guestId, bookingId: o.bookingId, types: types }
		});

		return {
			authorizedConsents: bookingIdGuestIdTypes
		} as ITravelQuestionnairePayload;
	}

	saveTravelQuestionnaire(guestId: number, bookingId: number, types: ITypes[]): Observable<boolean> {
		const payload: ISaveTravelQuestionnairePayload = {
			questionnaire: [{
				guestReservationId: {
					bookingId: bookingId,
					guestId: guestId
				},
				types: types
			}]
		};
		return this.microServices.createUpdateTravelQuestionnaire(payload).pipe(map(r => {
			if (r.status === this.constants.HTTP_STATUS_CODE.SUCCESS) {
				const response = {
					status: r.status,
					errors: r.errors,
					payload: {
						successfulGrantees: _.get(r, 'r.payload.successfulGrantees', {}),
						failedGrantees: _.get(r, 'r.payload.failedGrantees', {}),
						questionnaire: payload.questionnaire
					}
				};
				this.store.dispatch(new CreateUpdateTravelQuestionnaireActionSuccess(response));
				return true;
			}
			return false;
		}));
	}

	mapToStoreFormat(authorizedConsentsResponse: any) {
		const granteesList = _.get(authorizedConsentsResponse, 'payload.grantees', []);
		const legalGrantorId = this.getLegalGrantor().guestId;
		let grantorId: any = null;

		if (!this.isGuestFromInternationalBookingOffice(legalGrantorId)) {
			grantorId = legalGrantorId;
		}
		else {
			const domesticGrantor = this.guestsService.getFirstDosmeticGuest();
			grantorId = domesticGrantor ? domesticGrantor.guestId : legalGrantorId;
		}
		const grantorContracts = granteesList.find((g: any) => g.guestReservationId.guestId === grantorId);
		const allContractsContent = this.allContractsContent(grantorContracts);
		const grantor = this.grantorInformation();
		const guestDocumentsList = this.mapGuestDocumentsListType(grantorContracts);

		return Object.assign({}, authorizedConsentsResponse, {
			allContractsContent: allContractsContent,
			grantorInfo: grantor,
			guestDocumentsListType: guestDocumentsList
		});
	}

	allContractsContent(grantorContracts: IAuthorizedConsentsGrantee) {
		const contractDictionary = {};
		this.contractsList.forEach(contractName => {
			const contractNameUnderscore = utils.addUnderscore(contractName);
			(contractDictionary as any)[contractNameUnderscore] = '';
			let doc = grantorContracts.signedDocuments ? grantorContracts.signedDocuments.find(c => c.type === contractName) : null;

			if (!doc) {
				doc = grantorContracts.unsignedDocuments ? grantorContracts.unsignedDocuments.find(c => c.type === contractName) : null;
			}
			if (doc) {
				(contractDictionary as any)[contractNameUnderscore] = this.mapContractContent(doc.contents);
			}
		});
		return contractDictionary;
	}

	mapContractContent(contents: IContent[]): string {
		let fullText = '';
		contents.forEach(c => {
			fullText = fullText + c.contentText;
		});
		return fullText;
	}

	mapGuestDocumentsListType(grantorContracts: IAuthorizedConsentsGrantee): any[] {
		const guestDocumentsListType: any = [];
		this.contractsList.forEach(contractName => {
			const contractNameUnderscore = utils.addUnderscore(contractName)
			let doc = grantorContracts.signedDocuments ? grantorContracts.signedDocuments.find(c => c.type === contractName) : null;
			if (!doc) {
				doc = grantorContracts.unsignedDocuments ? grantorContracts.unsignedDocuments.find(c => c.type === contractName) : null;
			}
			if (doc) {
				return guestDocumentsListType.push({
					documentType: contractNameUnderscore,
					guestDocumentsList: this.mapGuestDocumentsList(doc.contents)
				});
			}
		});
		return guestDocumentsListType;
	}

	mapGuestDocumentsList(contents: IContent[]): any[] {
		const docList: any = [];
		contents.forEach(c => {
			docList.push({
				contentId: c.id,
				contentLanguageCode: c.languageCode,
				contentSequenceNumber: c.sequence,
				contentVersionNumber: c.version
			})
		});
		return docList;
	}

	get ContractsContent(): {} {
		let contracts: any = {};
		this.store
			.pipe(
				select(fromRoot.getContractsContent),
				take(1)
			)
			.subscribe(c => {
				contracts = c;
			}
			)
		return contracts;
	}

}
